import type { PostPreview } from '@/types';

export interface SearchIndexItem {
  id: string;
  // [lowercased fields JSON, lowercased description, lowercased title] -
  // later positions carry more weight when scoring
  data: [string, string, string];
  preview: PostPreview;
}

interface SearchIndex {
  items: SearchIndexItem[];
}

let indexPromise: Promise<SearchIndex> | null = null;

function loadIndex(): Promise<SearchIndex> {
  if (!indexPromise) {
    indexPromise = fetch('/search-index.json').then((res) => {
      if (!res.ok) {
        indexPromise = null;
        throw new Error(`Failed to load search index: ${res.status}`);
      }
      return res.json();
    });
  }
  return indexPromise;
}

function countOccurrences(haystack: string, needle: string): number {
  let count = 0;
  let idx = haystack.indexOf(needle);
  while (idx !== -1) {
    count++;
    idx = haystack.indexOf(needle, idx + needle.length);
  }
  return count;
}

function score(item: SearchIndexItem, words: string[]): number {
  let total = 0;
  for (const word of words) {
    for (let i = 0; i < item.data.length; i++) {
      // weight by tuple position: body JSON x1, description x2, title x4
      total += countOccurrences(item.data[i], word) * Math.pow(2, i);
    }
  }
  return total;
}

export async function searchPosts(term: string): Promise<PostPreview[]> {
  if (!term || term.length < 3) {
    return [];
  }
  const words = term.toLowerCase().split(/\s+/).filter((w) => w.length > 0);
  if (words.length === 0) {
    return [];
  }
  const index = await loadIndex();
  return index.items
    .map((item) => ({ item, score: score(item, words) }))
    .filter((scored) => scored.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((scored) => scored.item.preview);
}
